import { App, type URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';

// Deep-link infrastructure (P0-OAUTH-01).
//
// Native auth callbacks (Google login, password recovery, research-connector OAuth) come back into
// the app as a deep link rather than a loaded page, so `detectSessionInUrl` is OFF on native
// (see client.ts) and we exchange the PKCE code here on `appUrlOpen`.
//
// Scheme + host:
//  - cbam://...                          custom scheme (in-app navigation, e.g. cbam://source/<id>)
//  - https://app.capeberkshire.co.uk/... verified App/Universal Link (HUMAN-GATED: Sam must serve the
//    .well-known files + add this host's callback to the Supabase Auth + Google OAuth allow-lists).
export const APP_SCHEME = 'cbam://';
export const APP_LINK_HOST = 'app.capeberkshire.co.uk';

/**
 * Redirect target for supabase-js OAuth / password-reset / connector flows.
 * Native → the cbam:// deep link; web → the current origin (unchanged behaviour).
 */
export function getOAuthRedirect(path = 'auth/callback'): string {
  const clean = path.replace(/^\/+/, '');
  if (Capacitor.isNativePlatform()) return `${APP_SCHEME}${clean}`;
  return `${window.location.origin}/${clean}`;
}

function extractParam(url: string, key: string): string | null {
  // codes/tokens may sit in the query OR the fragment depending on the flow
  const m = url.match(new RegExp(`[?&#]${key}=([^&#]+)`));
  return m ? decodeURIComponent(m[1]) : null;
}

/** Map an incoming deep-link URL to an in-app route path, or null if it isn't a nav link. */
export function deepLinkToRoute(url: string): string | null {
  try {
    if (url.startsWith(APP_SCHEME)) {
      const rest = url.slice(APP_SCHEME.length).replace(/^\/+/, '');
      const path = '/' + rest.split('?')[0].split('#')[0];
      return path.length > 1 ? path : null;
    }
    const u = new URL(url);
    if (u.host === APP_LINK_HOST && u.pathname && u.pathname !== '/') return u.pathname + u.search;
    return null;
  } catch {
    return null;
  }
}

/**
 * Initialise the appUrlOpen dispatcher (native only). Returns a disposer.
 * @param navigate react-router navigate fn (so callbacks land on the right screen).
 */
export function initDeepLinks(navigate: (path: string) => void): () => void {
  if (!Capacitor.isNativePlatform()) return () => {};

  const handler = async (event: URLOpenListenerEvent) => {
    const url = event.url;

    // 1) Auth callback: exchange the PKCE code for a session, then route to the app.
    const code = extractParam(url, 'code');
    const isRecovery = url.includes('type=recovery');
    if (code) {
      try {
        await supabase.auth.exchangeCodeForSession(code);
      } catch {
        /* recovery / already-consumed codes are handled by the ResetPassword screen */
      }
      navigate(isRecovery ? '/reset-password' : '/partner/dashboard');
      return;
    }

    // 2) Plain in-app deep link (cbam://source/<id>, https app-link, …)
    const route = deepLinkToRoute(url);
    if (route) navigate(route);
  };

  const subPromise = App.addListener('appUrlOpen', handler);
  return () => {
    void subPromise.then((s) => s.remove());
  };
}
